import { Box, Stack, SxProps, Theme, useTheme } from "@mui/material";
import { Typography as FoodsTypography, FoodsIcon } from "@foodpilot/foods";
import type { IconProps } from "./index";

export const IconComponent = (props: IconProps) => {
    const theme = useTheme();
    const {
        icon,
        size = 3,
        textSize,
        color,
        url,
        target = "_blank",
        hoverEffect = true,
        alignment = "center",
        text,
        textPosition = "right",
        textColor,
    } = props;

    const formattedUrl =
        url ?
            url.startsWith("http") ?
                url
            :   `https://${url}`
        :   undefined;

    const alignmentMap = {
        left: "flex-start",
        center: "center",
        right: "flex-end",
    };

    const directionMap = {
        left: "row-reverse",
        right: "row",
        top: "column-reverse",
        bottom: "column",
    };

    const IconContent = (
        <Stack
            direction={["left", "right"].includes(textPosition) ? "row" : "column"}
            spacing={1}
            alignItems="center"
            sx={
                {
                    flexDirection: directionMap[textPosition],
                    width: "fit-content",
                    margin: "0 !important", // Ensure margin doesn't override parent alignment
                    display: "inline-flex !important",
                    alignSelf:
                        alignment === "center" ? "center"
                        : alignment === "right" ? "flex-end"
                        : "flex-start",
                    transition: "transform 0.3s ease",
                    color: color,
                } as SxProps<Theme>
            }
        >
            <FoodsIcon icon={icon} size={size / 4} />

            {text && (
                <FoodsTypography
                    variant="body1"
                    sx={{
                        fontSize: textSize || theme.typography.fontSize,
                        color: textColor,
                        fontWeight: theme.typography.fontWeightMedium,
                    }}
                >
                    {text}
                </FoodsTypography>
            )}
        </Stack>
    );

    return (
        <Box
            className={`icon-align-${alignment}`}
            sx={{
                display: "flex",
                justifyContent: alignmentMap[alignment],
                alignItems: "center",
                width: "100%",
                textAlign: alignment,
                "& > *": {
                    margin:
                        alignment === "center" ? "0 auto"
                        : alignment === "right" ? "0 0 0 auto"
                        : "0 auto 0 0",
                },
            }}
        >
            {formattedUrl ?
                <Box
                    component="a"
                    href={formattedUrl}
                    target={target}
                    rel={target === "_blank" ? "noopener noreferrer" : undefined}
                    sx={{
                        textDecoration: "none",
                        color,
                        cursor: "pointer",
                        display: "inline-flex",
                        margin:
                            alignment === "center" ? "0 auto !important"
                            : alignment === "right" ? "0 0 0 auto !important"
                            : "0 auto 0 0 !important",
                        alignSelf:
                            alignment === "center" ? "center"
                            : alignment === "right" ? "flex-end"
                            : "flex-start",
                        transition: hoverEffect ? "transform 0.3s ease, opacity 0.3s ease" : "none",
                        "&:hover":
                            hoverEffect ?
                                {
                                    transform: "scale(1.05)",
                                    opacity: 0.85,
                                    "& .MuiSvgIcon-root": {
                                        transform: "scale(1.1)",
                                    },
                                }
                            :   {},
                    }}
                >
                    {IconContent}
                </Box>
            :   IconContent}
        </Box>
    );
};

export default IconComponent;
